<template>
    <div>
        <div class="modal-body">
            <div class="row gutters">
                <div class="col-12">
                    <div class="form-group">
                        <label for="daysExpire" class="required">{{ t('DOCUMENTS.CRESCENT_DAY') }}:</label>
                        <input type="number" id="daysExpire" placeholder="Dias para acrescentar"
                               :min="minDays" :max="maxDays" step="1" inputmode="numeric"
                               :value="numberDays"
                               :disabled="isAtMaxDeadline"
                               :class="['form-control', {'is-invalid': daysError}]"
                               @keydown="blockNonDigitKeys"
                               @input="setNumberDays($event)"/>
                        <div class="invalid-feedback" v-if="daysError">{{ daysError }}</div>
                        <small class="form-text text-muted" v-else-if="!isAtMaxDeadline">Informe um valor entre {{ minDays }} e {{ maxDays }} dias.</small>
                    </div>
                </div>
                <div class="col-12" v-if="documentDeadline">
                    <div class="alert alert-warning mb-2" v-if="isAtMaxDeadline">
                        Este documento já está no prazo máximo permitido pela Clicksign
                        ({{ maxDaysFromCreation }} dias após a criação): {{ formatDate(documentMaxDeadline) }}.
                    </div>
                    <div class="small" v-else>
                        <div>Prazo atual: <strong>{{ formatDate(documentDeadline) }}</strong></div>
                        <div>Prazo máximo permitido: <strong>{{ formatDate(documentMaxDeadline) }}</strong></div>
                        <div v-if="newDeadline">Novo prazo: <strong>{{ formatDate(newDeadline) }}</strong></div>
                    </div>
                </div>
                <template v-if="idDocExpire === 'all'">
                    <div class="col-12 mb-2">
                        <div class="dropdown-divider"></div>
                        <div class="text-center font-weight-bolder" style="color: #0e5e4e">
                            Somente documentos que irão expirar entre:
                        </div>
                    </div>
                    <div class="col-6">
                        <div class="form-group">
                            <label for="start" class="required">{{ t('DOCUMENTS.PLUS_START_DATE') }}:</label>
                            <input type="date" id="start" placeholder="expirando entre inicio"
                                   v-model="start_date" class="form-control"
                                   :min="today" :max="maxDate"
                                   @change="verifyDate('start')"
                            />
                        </div>
                    </div>
                    <div class="col-6">
                        <div class="form-group">
                            <label for="end" class="required">{{ t('DOCUMENTS.PLUS_END_DATE') }}:</label>
                            <input type="date" id="end" placeholder="expirando entre final"
                                   :min="start_date || today" :max="maxDate"
                                   @change="verifyDate('end')"
                                   v-model="end_date" class="form-control"
                            />
                        </div>
                    </div>
                </template>
            </div>
        </div>
        <div class="modal-footer">
            <div class="d-flex flex-row">
                <button class="btn btn-secondary mr-1" @click="$emit('closeModal')">Fechar</button>
                <button class="btn btn-primary" :disabled="!canSave" @click="save">Salvar</button>
            </div>
        </div>
    </div>
</template>

<script>
import {useI18n} from 'vue-i18n';
import {useToast} from 'vue-toastification';
import {mapActions} from 'vuex';
import moment from 'moment';

export default {
    name: "modalPlusTimeExpirationComponent",
    setup() {
        const {t} = useI18n();
        const toast = useToast();
        return {t, toast}
    },
    props:['idDocExpire', 'docExpire'],
    data() {
        return {
            minDays: 1,
            maxDaysFromCreation: 90,
            today: moment().format('YYYY-MM-DD'),
            maxDate: moment().add(90, 'days').format('YYYY-MM-DD'),
            numberDays: null,
            start_date: moment().format('YYYY-MM-DD'),
            end_date: moment().add(1, 'days').format('YYYY-MM-DD'),
        }
    },
    emits:['closeModal'],
    computed:{
        documentDeadline()
        {
            if (this.idDocExpire === 'all' || !this.docExpire?.deadline_at || !this.docExpire?.max_deadline_at) {
                return null;
            }
            return this.toLocalDate(this.docExpire.deadline_at);
        },
        documentMaxDeadline()
        {
            return this.documentDeadline ? this.toLocalDate(this.docExpire.max_deadline_at) : null;
        },
        maxDays()
        {
            if (!this.documentDeadline) {
                return this.maxDaysFromCreation;
            }
            const remainingDays = this.documentMaxDeadline.diff(this.documentDeadline, 'days');
            return Math.max(0, Math.min(this.maxDaysFromCreation, remainingDays));
        },
        isAtMaxDeadline()
        {
            return this.maxDays < this.minDays;
        },
        newDeadline()
        {
            if (!this.documentDeadline || this.isAtMaxDeadline || this.numberDays === null || this.daysError) {
                return null;
            }
            return this.documentDeadline.clone().add(this.numberDays, 'days');
        },
        daysError()
        {
            if (this.numberDays === null || this.isAtMaxDeadline) {
                return null;
            }
            if (!Number.isInteger(this.numberDays) || this.numberDays < this.minDays || this.numberDays > this.maxDays) {
                return `O valor deve ser um número inteiro entre ${this.minDays} e ${this.maxDays}.`;
            }
            return null;
        },
        canSave()
        {
            return !this.isAtMaxDeadline && this.numberDays !== null && !this.daysError;
        },
    },
    methods:{
        ...mapActions({
            plusSingleDocumentExpiration:'plusSingleDocumentExpiration',
            plusAllDocumentExpiration:'plusAllDocumentExpiration',
        }),
        verifyDate(context)
        {
            if (context === 'start'){
                if (this.end_date && this.start_date > this.end_date){
                    this.start_date = this.end_date;
                    this.toast.error("Data inicial deve ser menor ou igual a data final");
                }else if (moment(this.start_date).diff(moment().format('YYYY-MM-DD'), 'days') > 90){
                    this.start_date = moment().format('YYYY-MM-DD');
                    this.toast.error("Data inicial deve conter data inferior a 90 dias a partir de hoje");
                }else if (moment(this.start_date).isBefore(moment(), 'day')){
                    this.start_date = moment().format('YYYY-MM-DD');
                    this.toast.error("Data inicial deve conter data superior ou igual a hoje");
                }
            }
            if (context === 'end'){
                if (this.start_date && this.end_date < this.start_date){
                    this.end_date = this.start_date;
                    this.toast.error("Data final deve ser maior ou igual a data inicial");
                }else if (moment(this.end_date).diff(moment().format('YYYY-MM-DD'), 'days') > 90){
                    this.end_date = moment().add(90, 'days').format('YYYY-MM-DD');
                    this.toast.error("Data final deve conter data inferior a 90 dias a partir de hoje");
                }else if (moment(this.end_date).isBefore(moment(), 'day')){
                    this.end_date = moment().add(90, 'days').format('YYYY-MM-DD');
                    this.toast.error("Data final deve conter data superior ou igual a hoje");
                }
            }
        },
        toLocalDate(value)
        {
            // Mantem o horario como enviado pelo backend, sem converter para o fuso do navegador
            return moment(moment.parseZone(value).format('YYYY-MM-DDTHH:mm:ss'));
        },
        formatDate(date)
        {
            return date.format('DD/MM/YYYY HH:mm');
        },
        blockNonDigitKeys(event)
        {
            if (['e', 'E', '+', '-', '.', ','].includes(event.key)) {
                event.preventDefault();
            }
        },
        setNumberDays(event)
        {
            const value = event.target.value.trim();
            if (value === '') {
                this.numberDays = null;
                return;
            }
            this.numberDays = /^\d+$/.test(value) ? Number(value) : value;
        },
        validationFields()
        {
            let valid = true;
            if (this.isAtMaxDeadline){
                valid = false;
                this.toast.error('Este documento já está no prazo máximo de expiração permitido.');
            }else if (this.numberDays === null){
                valid = false;
                this.toast.error('Necessario informar dias para acrescentar!')
            }else if (this.daysError){
                valid = false;
                this.toast.error(this.daysError);
            }
            if (this.idDocExpire === 'all'){
                if (!this.end_date || !this.start_date){
                    this.toast.error("Campo Data inicial e data final são obrigatórios");
                    return false
                }
                if (moment(this.end_date).isBefore(moment(), 'day')){
                    valid = false;
                    this.toast.error("Data final deve conter data superior ou igual a hoje");
                }
                if (moment(this.start_date).isBefore(moment(), 'day')){
                    valid = false;
                    this.toast.error("Data inicial deve conter data superior ou igual a hoje");
                }
                if (moment(this.end_date).diff(moment().format('YYYY-MM-DD'), 'days') > 90){
                    valid = false;
                    this.toast.error("Data final deve conter data inferior a 90 dias a partir de hoje");
                }
                if (moment(this.start_date).diff(moment().format('YYYY-MM-DD'), 'days') > 90){
                    valid = false;
                    this.toast.error("Data inicial deve conter data inferior a 90 dias a partir de hoje");
                }
                if (this.end_date < this.start_date){
                    valid = false;
                    this.toast.error("Data final deve ser maior ou igual a data inicial");
                }
            }
            return valid;
        },
        save(){
            if (this.validationFields()){
                if (this.idDocExpire === 'all'){
                    this.plusAllDocumentExpiration({day:this.numberDays, start_date:this.start_date, end_date:this.end_date});
                }else {
                    this.plusSingleDocumentExpiration({day:this.numberDays, item:this.docExpire});
                }
            }
        },
    }
}
</script>

<style scoped>

</style>
